import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { CheckCircle, XCircle, Spinner, ArrowRight } from "@phosphor-icons/react";
import { fmtEur } from "../lib/utils";

export default function PaymentSuccess() {
  const { t } = useI18n();
  const [sp] = useSearchParams();
  const [status, setStatus] = useState("polling");
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const sid = sp.get("session_id");
    if (!sid) { setStatus("error"); return; }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data: d } = await api.get(`/payments/checkout/status/${sid}`);
        setData(d);
        if (d.payment_status === "paid") { setStatus("paid"); return; }
        if (d.status === "expired") { setStatus("expired"); return; }
        if (attempts >= 10) { setStatus("timeout"); return; }
        attempts += 1;
        setTimeout(poll, 2000);
      } catch { setStatus("error"); }
    };
    poll();
  }, [sp]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[var(--tf-bg)]" data-testid="payment-result">
      <div className="tf-card p-10 max-w-lg w-full text-center">
        <div className="tf-cmyk-strip mb-8" />
        {status === "polling" && (
          <>
            <Spinner size={48} weight="bold" className="mx-auto animate-spin text-[var(--tf-primary)]" />
            <h2 className="font-display font-black text-2xl mt-6">{t("ps_verifying")}</h2>
            <p className="text-sm text-[var(--tf-muted)] mt-2">{t("ps_wait")}</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle size={64} weight="fill" className="mx-auto text-[var(--tf-success)]" />
            <h2 className="font-display font-black text-3xl mt-6">{t("ps_confirmed")}</h2>
            {data && data.kind === "branding_order" ? (
              <p className="text-sm text-[var(--tf-muted)] mt-2">{t("ps_branding_success")}</p>
            ) : (
              data && <p className="text-sm text-[var(--tf-muted)] mt-2">{t("ps_wallet_success", { amount: fmtEur(data.amount) })}</p>
            )}
            <button
              onClick={() => navigate(data?.kind === "branding_order" ? "/client/branding" : "/client")}
              className="tf-btn tf-btn-primary mt-8 w-full"
              data-testid="back-to-dash"
            >
              {data?.kind === "branding_order" ? t("ps_goto_branding") : t("ps_back_to_wallet")} <ArrowRight size={16} weight="bold" />
            </button>
          </>
        )}
        {(status === "error" || status === "expired" || status === "timeout") && (
          <>
            <XCircle size={64} weight="fill" className="mx-auto text-[var(--tf-error)]" />
            <h2 className="font-display font-black text-3xl mt-6">{t("ps_failed_title")}</h2>
            <p className="text-sm text-[var(--tf-muted)] mt-2">{t("ps_failed_desc")}</p>
            <button onClick={() => navigate("/client/recharge")} className="tf-btn tf-btn-ghost mt-8 w-full">{t("ps_retry")}</button>
          </>
        )}
      </div>
    </div>
  );
}
